// src/pages/PrivacyPolicy.jsx
export default function PrivacyPolicy() {
    const effectiveDate = "August 23, 2025";
    const lastUpdated = "August 23, 2025";
  
    const sections = [
      { id: "info-we-collect", title: "1. Information We Collect" },
      { id: "how-we-use", title: "2. How We Use Your Information" },
      { id: "sharing", title: "3. How We Share Your Information" },
      { id: "security", title: "4. Data Security" },
      { id: "your-rights", title: "5. Your Rights" },
      { id: "hipaa", title: "6. HIPAA Notice of Privacy Practices" },
      { id: "cookies", title: "7. Cookies & Website Tracking" },
      { id: "children", title: "8. Children’s Privacy" },
      { id: "changes", title: "9. Changes to This Policy" },
      { id: "contact", title: "10. Contact Us" },
    ];
  
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Hero */}
        <header className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
          <div className="max-w-6xl mx-auto px-6 py-12">
            <h1 className="text-4xl font-bold">Privacy Policy</h1>
            <p className="mt-2 text-white/90 max-w-3xl">
              Your privacy matters to us. This policy explains how AllWell
              Companion Home Care collects, uses, shares, and safeguards your
              information across our services and website.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-5">
              <span className="inline-flex items-center rounded-full bg-white/15 px-3 py-1 text-sm">
                <span className="mr-2">📅</span>
                Effective: {effectiveDate}
              </span>
              <span className="inline-flex items-center rounded-full bg-white/15 px-3 py-1 text-sm">
                <span className="mr-2">🛠</span>
                Last Updated: {lastUpdated}
              </span>
            </div>
          </div>
        </header>
  
        {/* Body */}
        <main className="max-w-6xl mx-auto px-6 py-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* TOC */}
            <aside className="lg:col-span-4 xl:col-span-3">
              <div className="lg:sticky lg:top-24">
                <div className="rounded-2xl border bg-white shadow-sm">
                  <div className="px-5 py-4 border-b">
                    <h2 className="text-sm font-semibold text-gray-700">
                      Table of Contents
                    </h2>
                  </div>
                  <nav className="p-2">
                    <ul className="space-y-1">
                      {sections.map((s) => (
                        <li key={s.id}>
                          <a
                            href={`#${s.id}`}
                            className="block rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            {s.title}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </nav>
                </div>
  
                <div className="mt-4 flex gap-3">
                  <button
                    onClick={() => window.print()}
                    className="flex-1 rounded-lg bg-white border shadow-sm px-4 py-2 text-sm hover:bg-gray-50"
                    aria-label="Print this policy"
                  >
                    🖨 Print
                  </button>
                  <a
                    href="#top"
                    className="flex-1 rounded-lg bg-white border shadow-sm px-4 py-2 text-sm text-center hover:bg-gray-50"
                  >
                    ↑ Top
                  </a>
                </div>
              </div>
            </aside>
  
            {/* Content */}
            <article className="lg:col-span-8 xl:col-span-9">
              {/* Top notice */}
              <div
                id="top"
                className="rounded-2xl border bg-white shadow-sm p-6 md:p-8 mb-6"
              >
                <p className="text-gray-700">
                  AllWell Companion Home Care (“Company,” “we,” “our,” or “us”) is
                  committed to protecting the privacy and confidentiality of the
                  personal information we collect from our clients, caregivers,
                  job applicants, and website visitors. This Privacy Policy
                  explains how we collect, use, store, and protect your
                  information. We do <strong>not</strong> sell your personal
                  information.
                </p>
              </div>
  
              {/* 1 */}
              <section
                id="info-we-collect"
                className="rounded-2xl border bg-white shadow-sm p-6 md:p-8 mb-6 scroll-mt-24"
              >
                <h2 className="text-2xl font-bold mb-3">
                  1. Information We Collect
                </h2>
                <p className="text-gray-700">
                  We may collect the following types of personal information:
                </p>
  
                <div className="mt-4 grid md:grid-cols-3 gap-4">
                  <div className="rounded-xl border p-4">
                    <h3 className="font-semibold">From Clients & Families</h3>
                    <ul className="list-disc pl-5 mt-2 text-gray-700 space-y-1">
                      <li>Name, address, phone number, email</li>
                      <li>Date of birth, health information, care preferences</li>
                      <li>Billing and payment information</li>
                    </ul>
                  </div>
                  <div className="rounded-xl border p-4">
                    <h3 className="font-semibold">From Applicants & Employees</h3>
                    <ul className="list-disc pl-5 mt-2 text-gray-700 space-y-1">
                      <li>Contact details, employment history, references</li>
                      <li>SSN (last 4 digits), background & work eligibility</li>
                      <li>Employment records as needed</li>
                    </ul>
                  </div>
                  <div className="rounded-xl border p-4">
                    <h3 className="font-semibold">From Website Visitors</h3>
                    <ul className="list-disc pl-5 mt-2 text-gray-700 space-y-1">
                      <li>Contact form inputs (name, email, message)</li>
                      <li>Technical data (IP, browser type, cookies)</li>
                      <li>Usage analytics to improve the site</li>
                    </ul>
                  </div>
                </div>
              </section>
  
              {/* 2 */}
              <section
                id="how-we-use"
                className="rounded-2xl border bg-white shadow-sm p-6 md:p-8 mb-6 scroll-mt-24"
              >
                <h2 className="text-2xl font-bold mb-3">
                  2. How We Use Your Information
                </h2>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>Provide and coordinate home care services</li>
                  <li>Communicate about care plans, employment, or inquiries</li>
                  <li>Process payments and billing</li>
                  <li>Recruit, hire, and manage employees</li>
                  <li>Comply with state, federal, and HIPAA regulations</li>
                  <li>Improve our website, services, and experience</li>
                </ul>
                <p className="text-gray-700 mt-3">
                  We will <strong>never sell</strong> your personal information.
                </p>
              </section>
  
              {/* 3 */}
              <section
                id="sharing"
                className="rounded-2xl border bg-white shadow-sm p-6 md:p-8 mb-6 scroll-mt-24"
              >
                <h2 className="text-2xl font-bold mb-3">
                  3. How We Share Your Information
                </h2>
                <p className="text-gray-700">
                  We may share your information only in the following
                  circumstances:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-1 mt-2">
                  <li>
                    With healthcare providers, care coordinators, or insurers/MCOs
                    as necessary for your care
                  </li>
                  <li>
                    With vetted service providers (e.g., payroll, IT) under
                    confidentiality obligations
                  </li>
                  <li>
                    When required by law, regulation, court order, or government
                    authority
                  </li>
                  <li>With your explicit consent</li>
                </ul>
              </section>
  
              {/* 4 */}
              <section
                id="security"
                className="rounded-2xl border bg-white shadow-sm p-6 md:p-8 mb-6 scroll-mt-24"
              >
                <h2 className="text-2xl font-bold mb-3">4. Data Security</h2>
                <p className="text-gray-700">
                  We implement reasonable administrative, technical, and physical
                  safeguards to protect your information from unauthorized access,
                  disclosure, alteration, or loss. However, no system is 100%
                  secure, and we cannot guarantee absolute security.
                </p>
              </section>
  
              {/* 5 */}
              <section
                id="your-rights"
                className="rounded-2xl border bg-white shadow-sm p-6 md:p-8 mb-6 scroll-mt-24"
              >
                <h2 className="text-2xl font-bold mb-3">5. Your Rights</h2>
                <p className="text-gray-700">
                  Depending on your location, you may have rights to:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-1 mt-2">
                  <li>Access and request a copy of your information</li>
                  <li>Request corrections or updates</li>
                  <li>
                    Request deletion, subject to legal/regulatory requirements
                  </li>
                  <li>Opt out of certain communications or tracking</li>
                  <li>
                    For California residents: exercise rights under the CCPA,
                    including “Do Not Sell My Personal Information”
                  </li>
                </ul>
              </section>
  
              {/* 6 */}
              <section
                id="hipaa"
                className="rounded-2xl border bg-white shadow-sm p-6 md:p-8 mb-6 scroll-mt-24"
              >
                <h2 className="text-2xl font-bold mb-3">
                  6. HIPAA Notice of Privacy Practices
                </h2>
                <p className="text-gray-700">
                  When we provide care services, we are also bound by the Health
                  Insurance Portability and Accountability Act (HIPAA). We
                  maintain the confidentiality of Protected Health Information
                  (PHI) and only use/disclose it as permitted or required by law.
                  You may request a copy of our full HIPAA Notice of Privacy
                  Practices at any time.
                </p>
              </section>
  
              {/* 7 */}
              <section
                id="cookies"
                className="rounded-2xl border bg-white shadow-sm p-6 md:p-8 mb-6 scroll-mt-24"
              >
                <h2 className="text-2xl font-bold mb-3">
                  7. Cookies & Website Tracking
                </h2>
                <p className="text-gray-700">
                  Our website may use cookies and similar technologies to improve
                  user experience, analyze performance, and understand usage. You
                  can control cookie settings in your browser. For additional
                  details, see our Privacy Notice.
                </p>
              </section>
  
              {/* 8 */}
              <section
                id="children"
                className="rounded-2xl border bg-white shadow-sm p-6 md:p-8 mb-6 scroll-mt-24"
              >
                <h2 className="text-2xl font-bold mb-3">8. Children’s Privacy</h2>
                <p className="text-gray-700">
                  Our services are intended for adults and seniors. We do not
                  knowingly collect personal information from children under 13.
                </p>
              </section>
  
              {/* 9 */}
              <section
                id="changes"
                className="rounded-2xl border bg-white shadow-sm p-6 md:p-8 mb-6 scroll-mt-24"
              >
                <h2 className="text-2xl font-bold mb-3">
                  9. Changes to This Policy
                </h2>
                <p className="text-gray-700">
                  We may update this Privacy Policy from time to time. Updates
                  will be posted on our website with a revised “Last Updated”
                  date. Material changes will be highlighted where appropriate.
                </p>
              </section>
  
              {/* 10 */}
              <section
                id="contact"
                className="rounded-2xl border bg-white shadow-sm p-6 md:p-8 mb-2 scroll-mt-24"
              >
                <h2 className="text-2xl font-bold mb-3">10. Contact Us</h2>
                <div className="text-gray-700 space-y-1">
                  <p className="font-semibold">AllWell Companion Home Care LLC</p>
                  <p>📍 Schaumburg, Illinois</p>
                  <p>📧 info@allwellcompanionhomecare.com</p>
                  <p>📞 630.339.9000</p>
                </div>
              </section>
  
              {/* <div className="text-sm text-gray-500 mt-6">
                © {new Date().getFullYear()} AllWell Companion Home Care LLC. All
                rights reserved.
              </div> */}
            </article>
          </div>
        </main>
      </div>
    );
  }
  